import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Shield, ShieldCheck, Star, Clock, DollarSign, MapPin,
  FileCheck, Upload, Loader2, CheckCircle2, AlertTriangle,
  Clipboard, User, Award, Briefcase, Calendar
} from 'lucide-react';
import api from '@/utils/api';

// Inspector specializations
const SPECIALIZATIONS = [
  { id: 'vehicle', label: 'Vehicles & Auto', icon: '🚗', requiresCert: true },
  { id: 'equipment', label: 'Equipment', icon: '🔧', requiresCert: true },
  { id: 'electronics', label: 'Electronics', icon: '📱', requiresCert: true },
  { id: 'machinery', label: 'Machinery', icon: '⚙️', requiresCert: true },
  { id: 'real_estate', label: 'Real Estate', icon: '🏠', requiresCert: true },
  { id: 'collectibles', label: 'Collectibles & Art', icon: '🎨', requiresCert: true },
  { id: 'general', label: 'General Merchandise', icon: '📦', requiresCert: true },
  { id: 'handyman', label: 'Handyman', icon: '🛠️', requiresCert: false }
];

// Certification types
const CERT_TYPES = [
  { id: 'government_cert', label: 'Government Certification' },
  { id: 'professional_license', label: 'Professional License' },
  { id: 'insurance', label: 'Professional Insurance' },
  { id: 'background_check', label: 'Background Check' },
  { id: 'training_cert', label: 'Training Certificate' },
  { id: 'portfolio', label: 'Portfolio/Experience Proof' }
];

/**
 * Inspector Card - Displays inspector info for listings
 */
export const InspectorCard = ({ inspector, onSelect, selected = false }) => {
  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${selected ? 'ring-2 ring-emerald-500' : ''}`}
      onClick={() => onSelect?.(inspector)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12">
            <AvatarImage src={inspector.profile_image} />
            <AvatarFallback className="bg-emerald-100 text-emerald-700">
              {inspector.name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold">{inspector.name}</h4>
              {inspector.verification_status === 'verified' && (
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Star className="h-3 w-3 text-amber-500" />
              <span>{inspector.rating?.toFixed(1) || 'New'}</span>
              <span>•</span>
              <span>{inspector.completed_inspections || 0} inspections</span>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {inspector.specializations?.slice(0, 3).map(spec => (
                <Badge key={spec} variant="outline" className="text-xs">
                  {SPECIALIZATIONS.find(s => s.id === spec)?.icon} {spec}
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm">
              {inspector.hourly_rate && (
                <span className="text-emerald-600 font-medium">
                  ${inspector.hourly_rate}/hr
                </span>
              )}
              {inspector.flat_fee && (
                <span className="text-blue-600 font-medium">
                  ${inspector.flat_fee} flat
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Request Inspection Modal - For buyers to request inspection
 */
export const RequestInspectionModal = ({ isOpen, onClose, listing, onSuccess }) => {
  const [inspectors, setInspectors] = useState([]);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inspectionType, setInspectionType] = useState('standard');
  const [useFlat, setUseFlat] = useState(false);
  const [preferredDate, setPreferredDate] = useState('');
  const [notes, setNotes] = useState('');
  const [estimatedHours, setEstimatedHours] = useState(2);
  
  useEffect(() => {
    if (isOpen && listing) {
      fetchInspectors();
    }
  }, [isOpen, listing]);
  
  const fetchInspectors = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/listings/${listing.listing_id}/inspection-options`);
      setInspectors(res.data.inspectors || []);
    } catch (error) {
      toast.error('Failed to load inspectors');
    } finally {
      setLoading(false);
    }
  };
  
  const calculateFee = () => {
    if (!selectedInspector) return 0;
    
    const multiplier = { express: 1.5, detailed: 1.25, standard: 1.0 }[inspectionType] || 1.0;
    
    if (useFlat && selectedInspector.flat_fee) {
      return selectedInspector.flat_fee * multiplier;
    } else if (selectedInspector.hourly_rate) {
      return selectedInspector.hourly_rate * estimatedHours * multiplier;
    }
    return selectedInspector.flat_fee || 100;
  };
  
  const handleSubmit = async () => {
    if (!selectedInspector) {
      toast.error('Please select an inspector');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post('/inspections/request', {
        listing_id: listing.listing_id,
        inspector_id: selectedInspector.inspector_id,
        inspection_type: inspectionType,
        preferred_date: preferredDate,
        notes,
        use_flat_fee: useFlat,
        estimated_hours: estimatedHours
      });
      
      toast.success('Inspection requested! Wait for inspector to accept.');
      onSuccess?.(res.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request inspection');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clipboard className="h-5 w-5 text-emerald-500" />
            Request Inspection
          </DialogTitle>
          <DialogDescription>
            Hire a certified inspector to verify this item before purchasing
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Item Info */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            {listing?.images?.[0] && (
              <img src={listing.images[0]} alt="" className="w-16 h-16 object-cover rounded" />
            )}
            <div>
              <p className="font-medium">{listing?.title}</p>
              <p className="text-sm text-slate-500">${listing?.price?.toFixed(2)}</p>
            </div>
          </div>
          
          {/* Inspector Selection */}
          <div>
            <Label className="text-base font-semibold">Select an Inspector</Label>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
              </div>
            ) : inspectors.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <User className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p>No verified inspectors available for this category</p>
              </div>
            ) : (
              <div className="grid gap-3 mt-2">
                {inspectors.map(inspector => (
                  <InspectorCard
                    key={inspector.inspector_id}
                    inspector={inspector}
                    selected={selectedInspector?.inspector_id === inspector.inspector_id}
                    onSelect={setSelectedInspector}
                  />
                ))}
              </div>
            )}
          </div>
          
          {selectedInspector && (
            <>
              {/* Inspection Type */}
              <div>
                <Label>Inspection Type</Label>
                <Select value={inspectionType} onValueChange={setInspectionType}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Inspection</SelectItem>
                    <SelectItem value="detailed">Detailed Inspection (+25%)</SelectItem>
                    <SelectItem value="express">Express Inspection (+50%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Pricing Option */}
              {selectedInspector.hourly_rate && selectedInspector.flat_fee && (
                <div className="flex items-center gap-4">
                  <Label>Pricing:</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={!useFlat} 
                      onCheckedChange={() => setUseFlat(false)}
                    />
                    <span className="text-sm">Hourly (${selectedInspector.hourly_rate}/hr)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={useFlat} 
                      onCheckedChange={() => setUseFlat(true)}
                    />
                    <span className="text-sm">Flat Fee (${selectedInspector.flat_fee})</span>
                  </div>
                </div>
              )}
              
              {!useFlat && selectedInspector.hourly_rate && (
                <div>
                  <Label>Estimated Hours</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={estimatedHours}
                    onChange={(e) => setEstimatedHours(parseInt(e.target.value) || 2)}
                    className="mt-1 w-24"
                  />
                </div>
              )}
              
              {/* Preferred Date */}
              <div>
                <Label>Preferred Date (Optional)</Label>
                <Input
                  type="date"
                  value={preferredDate}
                  onChange={(e) => setPreferredDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              {/* Notes */}
              <div>
                <Label>Special Instructions (Optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any specific areas to focus on..."
                  className="mt-1"
                />
              </div>
              
              {/* Fee Summary */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Estimated Fee:</span>
                  <span className="text-xl font-bold text-emerald-700">${calculateFee().toFixed(2)}</span>
                </div>
                <p className="text-xs text-emerald-600 mt-1">
                  Payment held in escrow until inspection report is submitted
                </p>
              </div>
            </>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={!selectedInspector || submitting}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Clipboard className="h-4 w-4 mr-2" />
            )}
            Request Inspection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Inspector Registration Modal
 */
export const InspectorRegistrationModal = ({ isOpen, onClose, onSuccess, existingProfile = null }) => {
  const [step, setStep] = useState(1); // 1: basic info, 2: certifications, 3: pricing
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: existingProfile?.name || '',
    specializations: existingProfile?.specializations || [],
    service_areas: existingProfile?.service_areas?.join(', ') || '',
    bio: existingProfile?.bio || '',
    years_experience: existingProfile?.years_experience || 0,
    hourly_rate: existingProfile?.hourly_rate || '',
    flat_fee: existingProfile?.flat_fee || ''
  });
  
  // Check if only handyman is selected (no cert required)
  const onlyHandymanSelected = formData.specializations.length === 1 && formData.specializations[0] === 'handyman';
  const hasHandymanSelected = formData.specializations.includes('handyman');
  
  const handleSpecToggle = (specId) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(specId)
        ? prev.specializations.filter(s => s !== specId)
        : [...prev.specializations, specId]
    }));
  };
  
  const handleSubmit = async () => {
    // For existing inspectors adding specializations, name is already set
    if (!existingProfile && !formData.full_name.trim()) {
      toast.error('Please enter your full name as it appears on your certification');
      return;
    }
    if (formData.specializations.length === 0) {
      toast.error('Please select at least one specialization');
      return;
    }
    if (!existingProfile && !formData.hourly_rate && !formData.flat_fee) {
      toast.error('Please set at least one pricing option');
      return;
    }
    
    setLoading(true);
    try {
      // Different endpoint for adding specializations vs new registration
      if (existingProfile) {
        // Add specializations to existing profile
        const res = await api.put('/inspector/profile', {
          specializations: formData.specializations
        });
        toast.success('Specializations updated successfully!');
      } else {
        // New registration
        const res = await api.post('/inspector/register', {
          ...formData,
          name: formData.full_name.trim(),
          service_areas: formData.service_areas.split(',').map(s => s.trim()).filter(Boolean),
          hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
          flat_fee: formData.flat_fee ? parseFloat(formData.flat_fee) : null,
          years_experience: parseInt(formData.years_experience) || 0
        });
        
        // If only handyman selected, auto-verify
        if (onlyHandymanSelected) {
          toast.success('Registered as Handyman! You can start accepting jobs now.');
        } else {
          toast.success('Registration submitted! Upload your certifications to get verified.');
        }
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-emerald-500" />
            {existingProfile ? 'Add More Specializations' : 'Become an Inspector'}
          </DialogTitle>
          <DialogDescription>
            {existingProfile 
              ? 'Expand your services by adding more specializations'
              : 'Register as a certified inspector to help buyers verify items'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Full Name - only for new registration */}
          {!existingProfile && (
            <div>
              <Label className="text-base font-semibold">Full Legal Name *</Label>
              <p className="text-sm text-slate-500 mb-2">Must match exactly with your certification documents</p>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Enter your full name as on certificate..."
                className="mt-1"
                data-testid="inspector-full-name-input"
              />
            </div>
          )}
          
          {/* Specializations */}
          <div>
            <Label className="text-base font-semibold">Specializations *</Label>
            <p className="text-sm text-slate-500 mb-2">
              {existingProfile ? 'Select the specializations you want to add' : 'Select all that apply'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {SPECIALIZATIONS.map(spec => {
                const isSelected = formData.specializations.includes(spec.id);
                const wasAlreadySelected = existingProfile?.specializations?.includes(spec.id);
                return (
                  <div
                    key={spec.id}
                    onClick={() => handleSpecToggle(spec.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors relative ${
                      isSelected
                        ? 'bg-emerald-50 border-emerald-500'
                        : 'bg-white hover:bg-slate-50'
                    }`}
                  >
                    {!spec.requiresCert && (
                      <span className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                        No Cert
                      </span>
                    )}
                    {wasAlreadySelected && (
                      <span className="absolute -top-2 -left-2 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                        Current
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      <span>{spec.icon}</span>
                      <span className="text-sm">{spec.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Handyman note */}
          {hasHandymanSelected && (
            <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700 border border-green-200">
              <p className="font-medium">Handyman - No Certification Required!</p>
              <p>You can start accepting general handyman jobs immediately without uploading certifications.</p>
            </div>
          )}
          
          {/* Service Areas - only for new registration */}
          {!existingProfile && (
            <div>
              <Label>Service Areas</Label>
              <Input
                value={formData.service_areas}
                onChange={(e) => setFormData(prev => ({ ...prev, service_areas: e.target.value }))}
                placeholder="New York, Los Angeles, Chicago..."
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Comma-separated cities/states</p>
            </div>
          )}
          
          {/* Experience - only for new registration */}
          {!existingProfile && (
            <div>
              <Label>Years of Experience</Label>
              <Input
                type="number"
                min={0}
                value={formData.years_experience}
                onChange={(e) => setFormData(prev => ({ ...prev, years_experience: e.target.value }))}
                className="mt-1 w-24"
              />
            </div>
          )}
          
          {/* Bio - only for new registration */}
          {!existingProfile && (
            <div>
              <Label>About You</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Describe your background and expertise..."
                className="mt-1"
                rows={3}
              />
            </div>
          )}
          
          {/* Pricing - only for new registration */}
          {!existingProfile && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                    placeholder="75"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Flat Fee ($)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.flat_fee}
                    onChange={(e) => setFormData(prev => ({ ...prev, flat_fee: e.target.value }))}
                    placeholder="150"
                    className="mt-1"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">Set at least one pricing option. You can offer both.</p>
            </>
          )}
          
          {/* Info Box */}
          {!existingProfile && !onlyHandymanSelected && (
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium mb-1">Next Steps</p>
              <p>After registration, you'll need to upload at least one certification document that matches your name for verification.</p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {existingProfile ? 'Update Specializations' : 'Register as Inspector'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Seller Verification Warning Badge
 */
export const SellerVerificationBadge = ({ sellerVerified, sellerEmailVerified }) => {
  if (sellerVerified) {
    return (
      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
        <ShieldCheck className="h-3 w-3 mr-1" />
        Verified Seller
      </Badge>
    );
  }
  
  if (sellerEmailVerified) {
    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
        <Shield className="h-3 w-3 mr-1" />
        Email Verified
      </Badge>
    );
  }
  
  return (
    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
      <AlertTriangle className="h-3 w-3 mr-1" />
      Unverified Seller
    </Badge>
  );
};

/**
 * Inspector Review Modal - For buyers to review inspectors after completed inspection
 */
export const InspectorReviewModal = ({ isOpen, onClose, inspection, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/inspections/${inspection.inspection_id}/review`, {
        rating,
        comment
      });
      toast.success('Thank you for your review!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            Rate Your Inspector
          </DialogTitle>
          <DialogDescription>
            How was your experience with {inspection?.inspector_name}?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Listing Info */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            {inspection?.listing_images?.[0] && (
              <img 
                src={inspection.listing_images[0]} 
                alt="" 
                className="w-12 h-12 rounded object-cover"
              />
            )}
            <div>
              <p className="font-medium text-sm">{inspection?.listing_title}</p>
              <p className="text-xs text-slate-500">Inspected by {inspection?.inspector_name}</p>
            </div>
          </div>
          
          {/* Star Rating */}
          <div className="text-center">
            <p className="text-sm text-slate-600 mb-3">Tap to rate</p>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star 
                    className={`h-10 w-10 ${
                      star <= (hoverRating || rating)
                        ? 'fill-amber-400 text-amber-400' 
                        : 'text-slate-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm mt-2 text-slate-600">
              {rating === 0 && 'Select a rating'}
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent!'}
            </p>
          </div>
          
          {/* Comment */}
          <div>
            <Label>Your Review (Optional)</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell others about your experience with this inspector..."
              rows={4}
              className="mt-1"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="bg-amber-500 hover:bg-amber-600"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Submit Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Inspector Reviews Display - Shows reviews for an inspector
 */
export const InspectorReviews = ({ inspectorId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ rating: 0, count: 0 });

  useEffect(() => {
    if (inspectorId) {
      fetchReviews();
    }
  }, [inspectorId]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inspectors/${inspectorId}/reviews`);
      setReviews(res.data.reviews || []);
      setStats({
        rating: res.data.inspector_rating || 0,
        count: res.data.review_count || 0
      });
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Rating Summary */}
      <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg">
        <div className="text-center">
          <div className="text-3xl font-bold text-amber-600">{stats.rating.toFixed(1)}</div>
          <div className="flex justify-center">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star 
                key={star}
                className={`h-4 w-4 ${star <= Math.round(stats.rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
              />
            ))}
          </div>
        </div>
        <div className="text-sm text-slate-600">
          Based on <strong>{stats.count}</strong> review{stats.count !== 1 ? 's' : ''}
        </div>
      </div>
      
      {/* Reviews List */}
      {reviews.length > 0 ? (
        <div className="space-y-3">
          {reviews.map((review) => (
            <div key={review.review_id} className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-slate-200 text-slate-600 text-sm">
                      {review.reviewer_name?.charAt(0)?.toUpperCase() || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium text-sm">{review.reviewer_name}</span>
                </div>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star}
                      className={`h-4 w-4 ${star <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
                    />
                  ))}
                </div>
              </div>
              {review.comment && (
                <p className="text-sm text-slate-600">{review.comment}</p>
              )}
              <p className="text-xs text-slate-400 mt-2">
                {new Date(review.created_at).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-slate-500">
          <Star className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          <p>No reviews yet</p>
        </div>
      )}
    </div>
  );
};

export default {
  InspectorCard,
  RequestInspectionModal,
  InspectorRegistrationModal,
  SellerVerificationBadge,
  InspectorReviewModal,
  InspectorReviews
};
